import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Navigate, Route, Routes } from 'react-router-dom';
import { resolveCashDenominationConfig, DEFAULT_CHANGE_MINIMUMS } from '@fixflow/shared-types';
import {
  getCurrentStore,
  listStores,
  sendNotificationTestToMe,
  updateCurrentStore,
  type NotificationTestTemplateKey,
  type StoreSettings,
  type StoreSettingsUpdate,
} from '@/lib/api/stores.js';
import { createUser, listUsers, updateUser, type UserListItem } from '@/lib/api/users.js';
import { cashSessionsApi, listCashSessions } from '@/lib/api/cash-sessions.js';
import { getWaivers, updateWaivers } from '@/lib/api/waivers.js';
import {
  getLoyaltySettings,
  updateLoyaltySettings,
  type LoyaltySettings as LoyaltySettingsData,
} from '@/lib/api/tenants.js';
import { formatCurrency, formatDate, formatDateTime, prettyLabel } from '@/lib/format.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';
import { CashSessionReportModal } from '@/components/cash/CashDrawerModal.js';
import { TicketTemplatesSettingsPage } from '@/pages/settings/TicketTemplatesSettingsPage.js';
import { RepairServicesListPage } from '@/pages/settings/RepairServicesListPage.js';
import { RepairServiceEditorPage } from '@/pages/settings/RepairServiceEditorPage.js';
import { EstimatedProfitVisibilitySettings } from '@/pages/settings/EstimatedProfitVisibilitySettings.js';
import { PosSettingsPage } from '@/pages/settings/PosSettingsPage.js';
import { SnippetsSettingsPage } from '@/pages/settings/SnippetsSettingsPage.js';
import { FormsSettingsPage } from '@/pages/settings/FormsSettingsPage.js';
import { FormBuilderPage } from '@/pages/settings/FormBuilderPage.js';
import { ProfileSettingsPage } from '@/pages/settings/ProfileSettingsPage.js';
import { CareGuidesSettingsPage } from '@/pages/settings/CareGuidesSettingsPage.js';
import { AuditLogSettingsPage } from '@/pages/settings/AuditLogSettingsPage.js';
import { PermissionsSettingsPage } from '@/pages/settings/PermissionsSettingsPage.js';
import { MigrationSettingsPage } from '@/pages/settings/MigrationSettingsPage.js';
import { DuplicateCustomersSettingsPage } from '@/pages/settings/DuplicateCustomersSettingsPage.js';
import { ChecklistItemsSettings } from '@/components/checklists/ChecklistItemsSettings.js';
import { useAuthStore } from '@/stores/auth.js';
import { SettingsLayout } from '@/layouts/SettingsLayout.js';
import { StoreInfoSettingsPage } from '@/pages/settings/StoreInfoSettingsPage.js';
import { PrintingSettingsPage } from '@/pages/settings/PrintingSettingsPage.js';
import { PaymentsSettingsPage } from '@/pages/settings/PaymentsSettingsPage.js';
import { IntegrationsSettingsPage } from '@/pages/settings/IntegrationsSettingsPage.js';
import { PinSettingsPage } from '@/pages/settings/PinSettingsPage.js';
import { LocationsSettingsPage } from '@/pages/settings/LocationsSettingsPage.js';
import { ConditionsSettingsPage } from '@/pages/settings/ConditionsSettingsPage.js';
import { DeviceCatalogSettingsPage } from '@/pages/settings/DeviceCatalogSettingsPage.js';
import { InventoryCategoriesSettingsPage } from '@/pages/settings/InventoryCategoriesSettingsPage.js';
import { SuppliersSettingsPage } from '@/pages/settings/SuppliersSettingsPage.js';
import { AppointmentsSettingsPage } from '@/pages/settings/AppointmentsSettingsPage.js';
import { FeePresetsSettingsPage } from '@/pages/settings/FeePresetsSettingsPage.js';
import { TicketStatusesSettingsPage } from '@/pages/settings/TicketStatusesSettingsPage.js';
import { DataCleanupBalancesPage } from '@/pages/settings/DataCleanupBalancesPage.js';
import { DeviceCompatibilityCleanupPage } from '@/pages/settings/DeviceCompatibilityCleanupPage.js';
import { InventoryDuplicatesPage } from '@/pages/settings/InventoryDuplicatesPage.js';
import { DataCleanupPartsCostsPage } from '@/pages/settings/DataCleanupPartsCostsPage.js';
import { DiscountPresetsSettingsPage } from '@/pages/settings/DiscountPresetsSettingsPage.js';
import { ApprovalsSettingsPage } from '@/pages/settings/ApprovalsSettingsPage.js';
import { PricingMatrixPage } from '@/pages/settings/PricingMatrixPage.js';
import { WarrantyRulesPage } from '@/pages/settings/WarrantyRulesPage.js';
import { UpsellSettingsPage } from '@/pages/settings/UpsellSettingsPage.js';
import {
  nullableText,
  positiveInteger,
  storeField,
  toBool,
  waiverCollectionModeValue,
} from '@/pages/settings/settings-utils.js';

const smsMergeFields = [
  '{device}',
  '{store_name}',
  '{ticket_number}',
  '{customer_name}',
  '{portal_link}',
  '{promised_date}',
  '{store_phone}',
  '{repair_services}',
];

function tempPassword() {
  return `TEMP-${Math.random().toString(36).slice(2, 10).toUpperCase()}`;
}

function TicketSettings() {
  const queryClient = useQueryClient();
  const storeQuery = useQuery({ queryKey: ['store-current'], queryFn: getCurrentStore });
  const store = storeQuery.data;
  const [form, setForm] = useState<Record<string, string | boolean | string[]>>({});
  const value = (key: keyof StoreSettings, fallback = '') => {
    const formValue = form[key];
    if (typeof formValue === 'boolean') return String(formValue);
    if (Array.isArray(formValue)) return formValue.join(',');
    return formValue ?? storeField(store, key, fallback);
  };
  const warrantyTypeValue =
    (typeof form.defaultWarrantyType === 'string' && form.defaultWarrantyType) ||
    store?.defaultWarrantyType ||
    'days';
  const boolValue = (key: keyof StoreSettings, fallback = false) =>
    typeof form[key] === 'boolean' ? Boolean(form[key]) : toBool(store?.[key], fallback);

  const mutation = useMutation({
    mutationFn: () => {
      const payload: StoreSettingsUpdate = {
        defaultRepairMinutes: positiveInteger(value('defaultRepairMinutes', '90'), 90),
        preWaiverRequired: boolValue('preWaiverRequired', true),
        preWaiverSkippableForReturning: boolValue('preWaiverSkippableForReturning', false),
        postWaiverRequired: boolValue('postWaiverRequired', true),
        waiverCollectionMode: waiverCollectionModeValue(
          value('waiverCollectionMode', 'customer_display'),
        ),
        intakeRequireImei: boolValue('intakeRequireImei', false),
        intakeRequireDevicePhoto: boolValue('intakeRequireDevicePhoto', false),
        defaultWarrantyType: warrantyTypeValue as 'days' | 'lifetime' | 'none',
        defaultWarrantyDays:
          warrantyTypeValue === 'lifetime' || warrantyTypeValue === 'none'
            ? 0
            : positiveInteger(value('defaultWarrantyDays', '90'), 90),
        overdueAlertHours: positiveInteger(value('overdueAlertHours', '48'), 48),
        lowStockNotifyEmail: nullableText(value('lowStockNotifyEmail')) ?? null,
        partsReservationMode: (value('partsReservationMode', 'disabled') ||
          'disabled') as 'disabled' | 'soft' | 'hard',
        partsReservationAutoReleaseDays: positiveInteger(
          value('partsReservationAutoReleaseDays', '7'),
          7,
        ),
        partsReservationShowAvailable: boolValue('partsReservationShowAvailable', true),
        autoAssignMode: (value('autoAssignMode', store?.autoAssignMode ?? 'off') ||
          'off') as 'off' | 'creator' | 'fewest_tickets' | 'least_work' | 'round_robin',
        autoAssignClockedInOnly: boolValue('autoAssignClockedInOnly', false),
        autoAssignExcludeRoles: Array.isArray(form.autoAssignExcludeRoles)
          ? (form.autoAssignExcludeRoles as string[])
          : Array.isArray(store?.autoAssignExcludeRoles)
            ? store!.autoAssignExcludeRoles!
            : [],
      };

      return updateCurrentStore(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['store-current'] });
      toastSuccess('Ticket settings saved');
    },
    onError: (error) => {
      const parsed = parseApiError(error);
      toastError(parsed.message, parsed.code);
    },
  });

  return (
    <div className="space-y-6">
      <form
        className="space-y-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
        onSubmit={(event) => {
          event.preventDefault();
          mutation.mutate();
        }}
      >
      <h2 className="text-xl font-bold text-slate-950">Ticket Settings</h2>
      <section className="space-y-4">
        <h3 className="font-semibold text-slate-950">Auto-assign new tickets</h3>
        <p className="text-sm text-slate-500">
          Applied when a ticket is created from POS, appointments, inquiries, or the customer portal.
          Imported tickets are not auto-assigned.
        </p>
        <fieldset className="space-y-2">
          {(
            [
              ['off', 'Off — assign manually'],
              ['creator', 'To whoever checked the customer in'],
              ['fewest_tickets', 'To the technician with the fewest open tickets'],
              ['least_work', 'To the technician with the least estimated remaining work'],
              ['round_robin', 'Round robin among technicians'],
            ] as const
          ).map(([mode, label]) => (
            <label key={mode} className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="autoAssignMode"
                checked={(form.autoAssignMode as string | undefined) === mode ||
                  (!(form.autoAssignMode as string | undefined) &&
                    (store?.autoAssignMode ?? 'off') === mode)}
                onChange={() => setForm((c) => ({ ...c, autoAssignMode: mode }))}
              />
              {label}
            </label>
          ))}
        </fieldset>
        <label className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3 text-sm font-medium">
          <span>Only assign to staff with an active (clocked-in) session</span>
          <input
            type="checkbox"
            checked={boolValue('autoAssignClockedInOnly', false)}
            onChange={(e) => setForm((c) => ({ ...c, autoAssignClockedInOnly: e.target.checked }))}
          />
        </label>
        <div>
          <p className="mb-2 text-sm font-medium text-slate-700">Exclude roles from auto-assign</p>
          <div className="flex flex-wrap gap-3 text-sm">
            {(['front_desk', 'technician', 'store_manager', 'tenant_admin'] as const).map((role) => {
              const selected = Array.isArray(form.autoAssignExcludeRoles)
                ? (form.autoAssignExcludeRoles as string[]).includes(role)
                : (store?.autoAssignExcludeRoles ?? []).includes(role);
              return (
                <label key={role} className="flex items-center gap-1.5 capitalize">
                  <input
                    type="checkbox"
                    checked={selected}
                    onChange={(e) => {
                      const prev = Array.isArray(form.autoAssignExcludeRoles)
                        ? [...(form.autoAssignExcludeRoles as string[])]
                        : [...(store?.autoAssignExcludeRoles ?? [])];
                      const next = e.target.checked
                        ? [...new Set([...prev, role])]
                        : prev.filter((r) => r !== role);
                      setForm((c) => ({ ...c, autoAssignExcludeRoles: next }));
                    }}
                  />
                  {role.replace(/_/g, ' ')}
                </label>
              );
            })}
          </div>
        </div>
      </section>

      <section className="space-y-4">
        <h3 className="font-semibold text-slate-950">Intake Defaults</h3>
        <div className="grid gap-4 md:grid-cols-2">
          <label className="block">
            <span className="text-sm font-medium">Default repair time (minutes)</span>
            <input
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
              type="number"
              value={value('defaultRepairMinutes', '90')}
              onChange={(e) => setForm((c) => ({ ...c, defaultRepairMinutes: e.target.value }))}
            />
          </label>
          <label className="block">
            <span className="text-sm font-medium">Waiver collection mode</span>
            <select
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
              value={value('waiverCollectionMode', 'customer_display')}
              onChange={(e) => setForm((c) => ({ ...c, waiverCollectionMode: e.target.value }))}
            >
              <option value="customer_display">Customer Display</option>
              <option value="hand_across">Hand Across iPad</option>
              <option value="sms_link">SMS Link</option>
            </select>
          </label>
        </div>
        {(
          [
            ['preWaiverRequired', 'Require pre-repair waiver', true],
            [
              'preWaiverSkippableForReturning',
              'Allow returning customers to skip pre-repair waiver',
              false,
            ],
            ['postWaiverRequired', 'Require post-repair waiver at checkout', true],
            ['intakeRequireImei', 'Require IMEI at intake', false],
            ['intakeRequireDevicePhoto', 'Require device photo at intake', false],
          ] as Array<[keyof StoreSettings, string, boolean]>
        ).map(([key, label, fallback]) => (
          <label
            key={key}
            className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3 text-sm font-medium"
          >
            <span>{label}</span>
            <input
              type="checkbox"
              checked={boolValue(key, fallback)}
              onChange={(e) => setForm((c) => ({ ...c, [key]: e.target.checked }))}
            />
          </label>
        ))}
      </section>

      <section className="space-y-4">
        <h3 className="font-semibold text-slate-950">Defaults & Alerts</h3>
        <div className="space-y-2">
          <span className="text-sm font-medium">Default warranty</span>
          <div className="flex flex-wrap items-center gap-4 text-sm">
            <label className="inline-flex items-center gap-2">
              <input
                type="radio"
                checked={warrantyTypeValue === 'none'}
                onChange={() =>
                  setForm((c) => ({ ...c, defaultWarrantyType: 'none', defaultWarrantyDays: '0' }))
                }
              />
              None
            </label>
            <label className="inline-flex items-center gap-2">
              <input
                type="radio"
                checked={warrantyTypeValue === 'days'}
                onChange={() =>
                  setForm((c) => ({
                    ...c,
                    defaultWarrantyType: 'days',
                    defaultWarrantyDays: value('defaultWarrantyDays', '90') || '90',
                  }))
                }
              />
              <input
                className="w-16 rounded-lg border border-slate-300 px-2 py-1"
                type="number"
                min={1}
                disabled={warrantyTypeValue !== 'days'}
                value={warrantyTypeValue === 'days' ? value('defaultWarrantyDays', '90') : ''}
                onChange={(e) =>
                  setForm((c) => ({
                    ...c,
                    defaultWarrantyType: 'days',
                    defaultWarrantyDays: e.target.value,
                  }))
                }
              />
              days
            </label>
            <label className="inline-flex items-center gap-2">
              <input
                type="radio"
                checked={warrantyTypeValue === 'lifetime'}
                onChange={() =>
                  setForm((c) => ({
                    ...c,
                    defaultWarrantyType: 'lifetime',
                    defaultWarrantyDays: '0',
                  }))
                }
              />
              Lifetime
            </label>
          </div>
          <span className="block text-xs text-slate-500">
            Applies unless overridden per service or pricing.
          </span>
        </div>
        <label className="block">
          <span className="text-sm font-medium">Overdue alert hours</span>
          <input
            className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
            type="number"
            value={value('overdueAlertHours', '48')}
            onChange={(e) => setForm((c) => ({ ...c, overdueAlertHours: e.target.value }))}
          />
        </label>
        <label className="block md:col-span-2">
          <span className="text-sm font-medium">Low stock alert email</span>
          <input
            className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
            value={value('lowStockNotifyEmail')}
            onChange={(e) => setForm((c) => ({ ...c, lowStockNotifyEmail: e.target.value }))}
          />
        </label>
      </section>

      <section className="space-y-4">
        <h3 className="font-semibold text-slate-950">Parts reservations</h3>
        <p className="text-sm text-slate-500">
          When enabled, adding inventory parts to a ticket reserves stock until the ticket is closed
          or the part is removed.
        </p>
        <label className="block">
          <span className="text-sm font-medium">Reservation mode</span>
          <select
            className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
            value={value('partsReservationMode', 'disabled')}
            onChange={(e) => setForm((c) => ({ ...c, partsReservationMode: e.target.value }))}
          >
            <option value="disabled">Disabled</option>
            <option value="soft">Soft (track reserved qty)</option>
            <option value="hard">Hard (same tracking; enforce later)</option>
          </select>
        </label>
        <label className="block">
          <span className="text-sm font-medium">Auto-release after (days)</span>
          <input
            className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
            type="number"
            min="0"
            value={value('partsReservationAutoReleaseDays', '7')}
            onChange={(e) =>
              setForm((c) => ({ ...c, partsReservationAutoReleaseDays: e.target.value }))
            }
          />
        </label>
        <label className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3 text-sm font-medium">
          <span>Show available qty (on hand − reserved) in inventory</span>
          <input
            type="checkbox"
            checked={boolValue('partsReservationShowAvailable', true)}
            onChange={(e) =>
              setForm((c) => ({ ...c, partsReservationShowAvailable: e.target.checked }))
            }
          />
        </label>
      </section>

      <button
        className="rounded-xl bg-primary px-4 py-2 font-semibold text-white disabled:opacity-50"
        disabled={mutation.isPending || storeQuery.isLoading}
      >
        Save Ticket Settings
      </button>
      </form>

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <h3 className="text-lg font-semibold text-slate-950">Customer Portal</h3>
        <p className="mt-1 text-sm text-slate-500">
          Share these links with customers or embed the status tracker on your website.
        </p>
        <div className="mt-4 space-y-3 text-sm">
          <div>
            <div className="font-medium text-slate-700">Portal URL</div>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <code className="rounded-lg bg-slate-100 px-2 py-1 text-xs">
                {`${window.location.origin}/portal/login`}
              </code>
              <button
                type="button"
                className="rounded-lg border border-slate-300 px-2 py-1 text-xs font-semibold"
                onClick={() => {
                  void navigator.clipboard.writeText(`${window.location.origin}/portal`);
                  toastSuccess('Copied');
                }}
              >
                Copy URL
              </button>
              <a
                href="/portal/login"
                target="_blank"
                rel="noreferrer"
                className="rounded-lg border border-slate-300 px-2 py-1 text-xs font-semibold"
              >
                Open →
              </a>
            </div>
          </div>
          <div>
            <div className="font-medium text-slate-700">Status Tracker Embed</div>
            <pre className="mt-1 overflow-x-auto rounded-lg bg-slate-100 p-2 text-[11px] leading-relaxed">
{`<iframe
  src="${window.location.origin}/track?store=${store?.slug ?? 'demo'}"
  width="400"
  height="500"
  frameborder="0"></iframe>`}
            </pre>
            <button
              type="button"
              className="mt-2 rounded-lg border border-slate-300 px-2 py-1 text-xs font-semibold"
              onClick={() => {
                const code = `<iframe src="${window.location.origin}/track?store=${store?.slug ?? 'demo'}" width="400" height="500" frameborder="0"></iframe>`;
                void navigator.clipboard.writeText(code);
                toastSuccess('iFrame code copied');
              }}
            >
              Copy iFrame Code
            </button>
          </div>
        </div>

        <div className="mt-5 space-y-3 border-t border-slate-100 pt-4">
          <label className="flex items-center justify-between text-sm font-medium">
            <span>Default: can create tickets</span>
            <input
              type="checkbox"
              checked={Boolean(store?.portalDefaultCanCreateTickets)}
              onChange={(e) =>
                updateCurrentStore({ portalDefaultCanCreateTickets: e.target.checked }).then(() => {
                  queryClient.invalidateQueries({ queryKey: ['store-current'] });
                  toastSuccess('Saved');
                })
              }
            />
          </label>
          <label className="block text-sm font-medium">
            Default estimate validity (hours)
            <input
              type="number"
              min={1}
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
              defaultValue={store?.estimateDefaultValidHours ?? 48}
              onBlur={(e) => {
                const hours = Number(e.target.value);
                if (!hours) return;
                void updateCurrentStore({ estimateDefaultValidHours: hours }).then(() => {
                  queryClient.invalidateQueries({ queryKey: ['store-current'] });
                  toastSuccess('Saved');
                });
              }}
            />
          </label>
          <label className="flex items-center justify-between text-sm font-medium">
            <span>Require signature on estimates</span>
            <input
              type="checkbox"
              checked={Boolean(store?.estimateRequireSignature)}
              onChange={(e) =>
                updateCurrentStore({ estimateRequireSignature: e.target.checked }).then(() => {
                  queryClient.invalidateQueries({ queryKey: ['store-current'] });
                  toastSuccess('Saved');
                })
              }
            />
          </label>
          <label className="block text-sm font-medium">
            Auto-send estimates via
            <select
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
              value={store?.estimateAutoSendVia ?? 'email'}
              onChange={(e) =>
                updateCurrentStore({
                  estimateAutoSendVia: e.target.value as 'email' | 'sms' | 'both' | 'none',
                }).then(() => {
                  queryClient.invalidateQueries({ queryKey: ['store-current'] });
                  toastSuccess('Saved');
                })
              }
            >
              <option value="email">Email</option>
              <option value="sms">SMS</option>
              <option value="both">Both</option>
              <option value="none">Don&apos;t send</option>
            </select>
          </label>
        </div>
      </section>
    </div>
  );
}

// Legacy RepairServiceSettings removed — see RepairServicesListPage / RepairServiceEditorPage

function EmailToggleWithTest({
  label,
  checked,
  onChecked,
  testTemplateKey,
}: {
  label: string;
  checked: boolean;
  onChecked: (value: boolean) => void;
  testTemplateKey: NotificationTestTemplateKey;
}) {
  const user = useAuthStore((s) => s.user);
  const isTenantAdmin = ['tenant_admin', 'platform_admin'].includes(user?.role ?? '');
  const testMut = useMutation({
    mutationFn: () => sendNotificationTestToMe({ templateKey: testTemplateKey }),
    onSuccess: (data) => {
      toastSuccess(
        data.dryRun
          ? `Dry-run: would email ${data.to} (ticket ${data.ticketNumber})`
          : `Test email sent to ${data.to} using ticket ${data.ticketNumber}`,
      );
    },
    onError: (error) => {
      const parsed = parseApiError(error);
      toastError(parsed.message, parsed.code);
    },
  });

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 text-sm font-medium">
      <label className="flex flex-1 items-center justify-between gap-3">
        <span>{label}</span>
        <input type="checkbox" checked={checked} onChange={(e) => onChecked(e.target.checked)} />
      </label>
      {isTenantAdmin ? (
        <button
          type="button"
          className="rounded-lg border border-slate-300 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          disabled={testMut.isPending}
          onClick={() => testMut.mutate()}
        >
          {testMut.isPending ? 'Sending…' : 'Send test to me'}
        </button>
      ) : null}
    </div>
  );
}

function SmsTemplateCard({
  title,
  description,
  enabled,
  template,
  onEnabled,
  onTemplate,
  testTemplateKey,
}: {
  title: string;
  description: string;
  enabled: boolean;
  template: string;
  onEnabled: (value: boolean) => void;
  onTemplate: (value: string) => void;
  testTemplateKey?: NotificationTestTemplateKey;
}) {
  const user = useAuthStore((s) => s.user);
  const isTenantAdmin = ['tenant_admin', 'platform_admin'].includes(user?.role ?? '');
  const testMut = useMutation({
    mutationFn: () =>
      sendNotificationTestToMe({
        templateKey: testTemplateKey!,
        templateOverride: template,
      }),
    onSuccess: (data) => {
      toastSuccess(
        data.dryRun
          ? `Dry-run: would send to ${data.to} (ticket ${data.ticketNumber})`
          : `Test sent to ${data.to} using ticket ${data.ticketNumber}`,
      );
    },
    onError: (error) => {
      const parsed = parseApiError(error);
      toastError(parsed.message, parsed.code);
    },
  });

  const countClass =
    template.length > 160
      ? 'text-red-600'
      : template.length > 140
        ? 'text-amber-600'
        : 'text-slate-500';
  return (
    <div className="rounded-2xl border border-slate-200 p-4">
      <label className="flex items-start justify-between gap-3">
        <span>
          <span className="block font-semibold text-slate-950">
            {enabled ? '✅' : '⬜'} {title}
          </span>
          <span className="text-sm text-slate-500">{description}</span>
        </span>
        <input type="checkbox" checked={enabled} onChange={(e) => onEnabled(e.target.checked)} />
      </label>
      <textarea
        className="mt-3 min-h-28 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
        value={template}
        onChange={(e) => onTemplate(e.target.value)}
      />
      <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
        <div className={`text-xs font-semibold ${countClass}`}>
          {template.length} characters. Available variables: {smsMergeFields.join(' ')}
        </div>
        {isTenantAdmin && testTemplateKey ? (
          <button
            type="button"
            className="rounded-lg border border-slate-300 px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            disabled={testMut.isPending}
            onClick={() => testMut.mutate()}
          >
            {testMut.isPending ? 'Sending…' : 'Send test to me'}
          </button>
        ) : null}
      </div>
    </div>
  );
}

function NotificationSettings() {
  const queryClient = useQueryClient();
  const storeQuery = useQuery({ queryKey: ['store-current'], queryFn: getCurrentStore });
  const store = storeQuery.data;
  const [form, setForm] = useState<Record<string, string | boolean>>({});
  const text = (key: keyof StoreSettings, fallback = '') => {
    const formValue = form[key];
    return typeof formValue === 'boolean'
      ? String(formValue)
      : (formValue ?? storeField(store, key, fallback));
  };
  const bool = (key: keyof StoreSettings, fallback = true) =>
    typeof form[key] === 'boolean' ? Boolean(form[key]) : toBool(store?.[key], fallback);
  const set = (key: keyof StoreSettings, value: string | boolean) =>
    setForm((current) => ({ ...current, [key]: value }));

  const mutation = useMutation({
    mutationFn: () => {
      const payload: StoreSettingsUpdate = {
        smsOnReady: bool('smsOnReady', true),
        smsOnWaitingParts: bool('smsOnWaitingParts', true),
        smsOnCancelled: bool('smsOnCancelled', true),
        smsOnCheckout: bool('smsOnCheckout', true),
        smsOnTicketCreated: bool('smsOnTicketCreated', true),
        smsTemplateReady: text('smsTemplateReady'),
        smsTemplateWaitingParts: text('smsTemplateWaitingParts'),
        smsTemplateCancelled: text('smsTemplateCancelled'),
        smsTemplateCheckout: text('smsTemplateCheckout'),
        smsTemplateTicketCreated: text('smsTemplateTicketCreated'),
        emailOnClosed: bool('emailOnClosed', true),
        emailOnCreate: bool('emailOnCreate', true),
        emailOnReady: bool('emailOnReady', true),
        sendCareGuideOnCheckout: bool('sendCareGuideOnCheckout', false),
        careGuideSendVia: (text('careGuideSendVia', 'both') as 'email' | 'sms' | 'both') || 'both',
      };

      return updateCurrentStore(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['store-current'] });
      toastSuccess('Notification settings saved');
    },
    onError: (error) => {
      const parsed = parseApiError(error);
      toastError(parsed.message, parsed.code);
    },
  });

  return (
    <form
      className="space-y-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
      onSubmit={(event) => {
        event.preventDefault();
        mutation.mutate();
      }}
    >
      <h2 className="text-xl font-bold text-slate-950">Notifications</h2>
      <p className="text-sm text-slate-500">
        Configure SMS and email templates. Integration credentials are managed under{' '}
        <a href="/settings/integrations" className="font-semibold text-primary hover:underline">
          Integrations
        </a>
        .
      </p>
      <section className="rounded-xl bg-slate-50 p-4">
        <h3 className="font-semibold text-slate-950">SMS Merge Fields</h3>
        <p className="mt-1 text-sm text-slate-600">
          Use these tokens in any SMS template: {smsMergeFields.join(' ')}
        </p>
      </section>
      <section className="space-y-4">
        <SmsTemplateCard
          title="Ticket Created Notification"
          description="Sent when a new repair ticket is created"
          enabled={bool('smsOnTicketCreated', true)}
          template={text(
            'smsTemplateTicketCreated',
            "Hi {customer_name}, your {device} is with {store_name}. We're working on: {repair_services}. Ticket #{ticket_number}",
          )}
          onEnabled={(v) => set('smsOnTicketCreated', v)}
          onTemplate={(v) => set('smsTemplateTicketCreated', v)}
          testTemplateKey="sms_ticket_created"
        />
        <SmsTemplateCard
          title="Repair Ready Notification"
          description="Sent when ticket status changes to Ready"
          enabled={bool('smsOnReady', true)}
          template={text(
            'smsTemplateReady',
            'Your {device} repair is ready for pickup at {store_name}! {portal_link}',
          )}
          onEnabled={(v) => set('smsOnReady', v)}
          onTemplate={(v) => set('smsTemplateReady', v)}
          testTemplateKey="sms_ready"
        />
        <SmsTemplateCard
          title="Waiting for Parts Notification"
          description="Sent when ticket is waiting for parts"
          enabled={bool('smsOnWaitingParts', true)}
          template={text(
            'smsTemplateWaitingParts',
            "We're waiting on a part for your repair — we'll update you soon.",
          )}
          onEnabled={(v) => set('smsOnWaitingParts', v)}
          onTemplate={(v) => set('smsTemplateWaitingParts', v)}
          testTemplateKey="sms_waiting_parts"
        />
        <SmsTemplateCard
          title="Ticket Cancelled Notification"
          description="Sent when a ticket is cancelled"
          enabled={bool('smsOnCancelled', true)}
          template={text(
            'smsTemplateCancelled',
            'Your repair ticket #{ticket_number} has been cancelled.',
          )}
          onEnabled={(v) => set('smsOnCancelled', v)}
          onTemplate={(v) => set('smsTemplateCancelled', v)}
          testTemplateKey="sms_cancelled"
        />
        <SmsTemplateCard
          title="Checkout/Receipt Notification"
          description="Sent after checkout"
          enabled={bool('smsOnCheckout', true)}
          template={text(
            'smsTemplateCheckout',
            'Thanks for visiting {store_name}! View your receipt: {portal_link}',
          )}
          onEnabled={(v) => set('smsOnCheckout', v)}
          onTemplate={(v) => set('smsTemplateCheckout', v)}
          testTemplateKey="sms_checkout"
        />
      </section>
      <section className="space-y-3 rounded-xl bg-slate-50 p-4">
        <h3 className="font-semibold text-slate-950">Email Notifications</h3>
        <p className="text-xs text-slate-500">
          Send test resolves merge fields from your most recent ticket and delivers to your login
          email.
        </p>
        {(
          [
            {
              key: 'emailOnCreate' as const,
              label: 'Send confirmation when ticket is created',
              testKey: 'email_create' as const,
            },
            {
              key: 'emailOnReady' as const,
              label: 'Send notification when repair is ready',
              testKey: 'email_ready' as const,
            },
            {
              key: 'emailOnClosed' as const,
              label: 'Send email receipt on checkout',
              testKey: 'email_closed' as const,
            },
          ] as const
        ).map((row) => (
          <EmailToggleWithTest
            key={row.key}
            label={row.label}
            checked={bool(row.key, true)}
            onChecked={(v) => set(row.key, v)}
            testTemplateKey={row.testKey}
          />
        ))}
      </section>
      <section className="rounded-xl border border-slate-200 bg-slate-50/80 p-4">
        <h3 className="font-semibold text-slate-950">Care Guides</h3>
        <p className="mt-1 text-sm text-slate-600">
          Automatically send post-repair care guides when a ticket is checked out.
        </p>
        <label className="mt-4 flex items-center justify-between rounded-xl bg-white px-4 py-3 text-sm font-medium">
          <span>Auto-send care guide on checkout</span>
          <input
            type="checkbox"
            checked={bool('sendCareGuideOnCheckout', false)}
            onChange={(e) => set('sendCareGuideOnCheckout', e.target.checked)}
          />
        </label>
        <label className="mt-3 block text-sm">
          <span className="font-medium text-slate-700">Send via</span>
          <select
            className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
            value={text('careGuideSendVia', 'both')}
            onChange={(e) => set('careGuideSendVia', e.target.value)}
          >
            <option value="both">Email & SMS</option>
            <option value="email">Email only</option>
            <option value="sms">SMS only</option>
          </select>
        </label>
      </section>
      <button
        className="rounded-xl bg-primary px-4 py-2 font-semibold text-white disabled:opacity-50"
        disabled={mutation.isPending || storeQuery.isLoading}
      >
        Save Notification Settings
      </button>
    </form>
  );
}

function WaiverSettings() {
  const queryClient = useQueryClient();
  const waiversQuery = useQuery({ queryKey: ['waivers'], queryFn: getWaivers });
  const [previewType, setPreviewType] = useState<'pre' | 'post' | null>(null);
  const [form, setForm] = useState({ preRepairContent: '', postRepairContent: '' });

  useEffect(() => {
    if (!waiversQuery.data) return;
    setForm({
      preRepairContent: waiversQuery.data.preRepair.content,
      postRepairContent: waiversQuery.data.postRepair.content,
    });
  }, [waiversQuery.data]);

  const mutation = useMutation({
    mutationFn: () => updateWaivers(form),
    onSuccess: (data) => {
      setForm({
        preRepairContent: data.preRepair.content,
        postRepairContent: data.postRepair.content,
      });
      queryClient.invalidateQueries({ queryKey: ['waivers'] });
      toastSuccess(
        `Waivers saved. Current versions: pre v${data.preRepair.version}, post v${data.postRepair.version}`,
      );
    },
    onError: (error) => {
      const parsed = parseApiError(error);
      toastError(parsed.message, parsed.code);
    },
  });

  const previewSource =
    previewType === 'pre'
      ? form.preRepairContent
      : previewType === 'post'
        ? form.postRepairContent
        : '';
  const preview = previewSource
    .replace(/\{store_name\}/g, 'Your Store')
    .replace(/\{customer_name\}/g, 'Jane Customer')
    .replace(/\{device\}/g, 'iPhone 14')
    .replace(/\{ticket_number\}/g, 'TKT-00123')
    .replace(/\{repair_description\}/g, 'Screen replacement')
    .replace(/\{quoted_price\}/g, '$149.99')
    .replace(/\{date\}/g, formatDate(new Date().toISOString()));

  return (
    <form
      className="space-y-5 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
      onSubmit={(event) => {
        event.preventDefault();
        mutation.mutate();
      }}
    >
      <div>
        <h2 className="text-xl font-bold text-slate-950">Waivers</h2>
        <p className="mt-1 text-sm text-slate-500">
          Last updated: {formatDate(waiversQuery.data?.updatedAt)}
        </p>
      </div>

      <div className="rounded-xl bg-amber-50 p-3 text-sm text-amber-800">
        Saving waiver content creates a new version. Existing signed waivers stay tied to the
        version they were signed under.
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <label className="block">
          <span className="font-semibold text-slate-950">
            Pre-repair waiver text
            <span className="ml-2 text-sm font-normal text-slate-500">
              v{waiversQuery.data?.preRepair.version ?? '1.0'}
            </span>
          </span>
          <textarea
            className="mt-2 min-h-[320px] w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
            value={form.preRepairContent}
            onChange={(event) =>
              setForm((current) => ({ ...current, preRepairContent: event.target.value }))
            }
            placeholder="Enter pre-repair waiver content..."
          />
          <button
            type="button"
            className="mt-2 rounded-xl border border-slate-300 px-3 py-2 text-sm font-semibold"
            onClick={() => setPreviewType('pre')}
          >
            Preview Pre-repair Waiver
          </button>
        </label>

        <label className="block">
          <span className="font-semibold text-slate-950">
            Post-repair waiver text
            <span className="ml-2 text-sm font-normal text-slate-500">
              v{waiversQuery.data?.postRepair.version ?? '1.0'}
            </span>
          </span>
          <textarea
            className="mt-2 min-h-[320px] w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
            value={form.postRepairContent}
            onChange={(event) =>
              setForm((current) => ({ ...current, postRepairContent: event.target.value }))
            }
            placeholder="Enter post-repair waiver content..."
          />
          <button
            type="button"
            className="mt-2 rounded-xl border border-slate-300 px-3 py-2 text-sm font-semibold"
            onClick={() => setPreviewType('post')}
          >
            Preview Post-repair Waiver
          </button>
        </label>
      </div>

      <p className="text-xs text-slate-500">
        Available merge fields:{' '}
        {
          '{store_name} {customer_name} {device} {ticket_number} {repair_description} {quoted_price} {date}'
        }
      </p>

      <button
        className="rounded-xl bg-primary px-4 py-2 font-semibold text-white disabled:opacity-50"
        disabled={mutation.isPending || waiversQuery.isLoading}
      >
        {mutation.isPending ? 'Saving...' : 'Save & Publish New Version'}
      </button>

      {previewType ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4">
          <div className="max-h-[80vh] w-full max-w-2xl overflow-auto rounded-2xl bg-white p-6 shadow-2xl">
            <h3 className="text-lg font-bold">
              {previewType === 'pre' ? 'Pre-repair' : 'Post-repair'} Waiver Preview
            </h3>
            <p className="mt-4 whitespace-pre-wrap text-sm text-slate-700">{preview}</p>
            <button
              type="button"
              className="mt-5 rounded-xl bg-primary px-4 py-2 font-semibold text-white"
              onClick={() => setPreviewType(null)}
            >
              Close
            </button>
          </div>
        </div>
      ) : null}
    </form>
  );
}

function LoyaltySettings() {
  const queryClient = useQueryClient();
  const loyaltyQuery = useQuery({ queryKey: ['tenant-loyalty'], queryFn: getLoyaltySettings });
  const storeQuery = useQuery({ queryKey: ['store-current'], queryFn: getCurrentStore });
  const loyalty = loyaltyQuery.data;
  const store = storeQuery.data;
  const [form, setForm] = useState<Record<string, string | boolean>>({});
  const [referral, setReferral] = useState({
    referralEnabled: false,
    referralReferrerPoints: '500',
    referralReferredPoints: '250',
    referralReferrerRewardType: 'points' as 'points' | 'store_credit',
    referralReferredRewardType: 'points' as 'points' | 'store_credit',
    referralReferrerCredit: '0',
    referralReferredCredit: '0',
    referralTrigger: 'first_visit' as 'first_visit' | 'first_purchase' | 'manual',
    referralNotifyReferrer: true,
  });

  useEffect(() => {
    if (!store) return;
    setReferral({
      referralEnabled: Boolean(store.referralEnabled),
      referralReferrerPoints:
        store.referralReferrerPoints != null ? String(store.referralReferrerPoints) : '500',
      referralReferredPoints:
        store.referralReferredPoints != null ? String(store.referralReferredPoints) : '250',
      referralReferrerRewardType:
        store.referralReferrerRewardType === 'store_credit' ? 'store_credit' : 'points',
      referralReferredRewardType:
        store.referralReferredRewardType === 'store_credit' ? 'store_credit' : 'points',
      referralReferrerCredit:
        store.referralReferrerCredit != null ? String(store.referralReferrerCredit) : '0',
      referralReferredCredit:
        store.referralReferredCredit != null ? String(store.referralReferredCredit) : '0',
      referralTrigger:
        (store.referralTrigger as 'first_visit' | 'first_purchase' | 'manual') ?? 'first_visit',
      referralNotifyReferrer: store.referralNotifyReferrer !== false,
    });
  }, [store]);
  const value = (key: keyof LoyaltySettingsData, fallback = '') => {
    const formValue = form[key];
    if (typeof formValue === 'boolean') return String(formValue);
    const savedValue = loyalty?.[key];
    return savedValue === null || savedValue === undefined
      ? fallback
      : String(formValue ?? savedValue);
  };
  const bool = (key: keyof LoyaltySettingsData, fallback = false) =>
    typeof form[key] === 'boolean' ? Boolean(form[key]) : toBool(loyalty?.[key], fallback);
  const disabled = !bool('loyaltyEnabled', false);
  const redemption = Number(value('loyaltyPointsPerDollarRedemption', '100')) || 100;
  const example = (500 / redemption).toFixed(2);
  const earnOptions: Array<[keyof LoyaltySettingsData, string]> = [
    ['loyaltyRepairsEarn', 'Repair services'],
    ['loyaltyRetailEarn', 'Retail / accessories'],
    ['loyaltyPartsEarn', 'Parts (internal use)'],
  ];
  const displayOptions: Array<[keyof LoyaltySettingsData, string]> = [
    ['loyaltyShowBalanceOnPortal', 'Show loyalty balance on customer portal'],
    ['loyaltyNotifyOnEarn', 'Notify customer of points earned at checkout'],
  ];
  const mutation = useMutation({
    mutationFn: () =>
      updateLoyaltySettings({
        loyaltyEnabled: bool('loyaltyEnabled', false),
        loyaltyEarnRatePerDollar: String(value('loyaltyEarnRatePerDollar', '1')),
        loyaltyPointsPerDollarRedemption: String(value('loyaltyPointsPerDollarRedemption', '100')),
        loyaltyMinPointsToRedeem: Number(value('loyaltyMinPointsToRedeem', '100')),
        loyaltyRepairsEarn: bool('loyaltyRepairsEarn', true),
        loyaltyRetailEarn: bool('loyaltyRetailEarn', true),
        loyaltyPartsEarn: bool('loyaltyPartsEarn', false),
        loyaltyShowBalanceOnPortal: bool('loyaltyShowBalanceOnPortal', true),
        loyaltyNotifyOnEarn: bool('loyaltyNotifyOnEarn', true),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-loyalty'] });
      toastSuccess('Loyalty program settings saved');
    },
    onError: (error) => {
      const parsed = parseApiError(error);
      toastError(parsed.message, parsed.code);
    },
  });

  const referralMutation = useMutation({
    mutationFn: () =>
      updateCurrentStore({
        referralEnabled: referral.referralEnabled,
        referralReferrerPoints: Number(referral.referralReferrerPoints) || 0,
        referralReferredPoints: Number(referral.referralReferredPoints) || 0,
        referralReferrerRewardType: referral.referralReferrerRewardType,
        referralReferredRewardType: referral.referralReferredRewardType,
        referralReferrerCredit: Number(referral.referralReferrerCredit) || 0,
        referralReferredCredit: Number(referral.referralReferredCredit) || 0,
        referralTrigger: referral.referralTrigger,
        referralNotifyReferrer: referral.referralNotifyReferrer,
      }),
    onSuccess: (updated) => {
      queryClient.setQueryData(['store-current'], updated);
      queryClient.invalidateQueries({ queryKey: ['store-current'] });
      toastSuccess('Referral settings saved');
    },
    onError: (error) => {
      const parsed = parseApiError(error);
      toastError(parsed.message, parsed.code);
    },
  });

  return (
    <div className="space-y-6">
    <form
      className="space-y-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
      onSubmit={(event) => {
        event.preventDefault();
        mutation.mutate();
      }}
    >
      <h2 className="text-xl font-bold text-slate-950">Loyalty & Referrals</h2>
      <label className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3 font-semibold">
        <span>Loyalty Program</span>
        <input
          type="checkbox"
          checked={bool('loyaltyEnabled', false)}
          onChange={(e) => setForm((c) => ({ ...c, loyaltyEnabled: e.target.checked }))}
        />
      </label>
      <fieldset disabled={disabled} className={disabled ? 'space-y-6 opacity-50' : 'space-y-6'}>
        <section className="grid gap-4 md:grid-cols-2">
          <label className="block">
            <span className="text-sm font-medium">Earn rate: $1 = points</span>
            <input
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
              value={value('loyaltyEarnRatePerDollar', '1')}
              onChange={(e) => setForm((c) => ({ ...c, loyaltyEarnRatePerDollar: e.target.value }))}
            />
          </label>
          <label className="block">
            <span className="text-sm font-medium">Minimum points to redeem</span>
            <input
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
              value={value('loyaltyMinPointsToRedeem', '100')}
              onChange={(e) => setForm((c) => ({ ...c, loyaltyMinPointsToRedeem: e.target.value }))}
            />
          </label>
        </section>
        <section className="space-y-2">
          {earnOptions.map(([key, label]) => (
            <label key={key} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={bool(key, key !== 'loyaltyPartsEarn')}
                onChange={(e) => setForm((c) => ({ ...c, [key]: e.target.checked }))}
              />{' '}
              {label}
            </label>
          ))}
        </section>
        <section className="space-y-3 rounded-xl bg-slate-50 p-4">
          <label className="block">
            <span className="text-sm font-medium">Points to redeem for $1.00</span>
            <input
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
              value={value('loyaltyPointsPerDollarRedemption', '100')}
              onChange={(e) =>
                setForm((c) => ({ ...c, loyaltyPointsPerDollarRedemption: e.target.value }))
              }
            />
          </label>
          <p className="text-sm text-slate-600">
            At current settings, 500 points = ${example} off.
          </p>
        </section>
        <section className="space-y-2">
          {displayOptions.map(([key, label]) => (
            <label
              key={key}
              className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3 text-sm font-medium"
            >
              <span>{label}</span>
              <input
                type="checkbox"
                checked={bool(key, true)}
                onChange={(e) => setForm((c) => ({ ...c, [key]: e.target.checked }))}
              />
            </label>
          ))}
        </section>
      </fieldset>
      <button
        className="rounded-xl bg-primary px-4 py-2 font-semibold text-white disabled:opacity-50"
        disabled={mutation.isPending || loyaltyQuery.isLoading}
      >
        Save Loyalty Settings
      </button>
    </form>

    <section className="space-y-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <h3 className="text-lg font-semibold text-slate-950">Referral Program</h3>
      <p className="text-sm text-slate-600">
        Reward referrers and new customers with loyalty points or store credit.
      </p>
      <label className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3 text-sm font-semibold">
        <span>Referral program enabled</span>
        <input
          type="checkbox"
          checked={referral.referralEnabled}
          onChange={(e) => setReferral((c) => ({ ...c, referralEnabled: e.target.checked }))}
        />
      </label>
      <fieldset
        disabled={!referral.referralEnabled}
        className={
          referral.referralEnabled
            ? 'grid gap-4 md:grid-cols-2'
            : 'grid gap-4 opacity-50 md:grid-cols-2'
        }
      >
        <label className="block">
          <span className="text-sm font-medium text-slate-700">Referrer reward type</span>
          <select
            className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
            value={referral.referralReferrerRewardType}
            onChange={(e) =>
              setReferral((c) => ({
                ...c,
                referralReferrerRewardType: e.target.value as 'points' | 'store_credit',
              }))
            }
          >
            <option value="points">Loyalty points</option>
            <option value="store_credit">Store credit</option>
          </select>
        </label>
        <label className="block">
          <span className="text-sm font-medium text-slate-700">Referred customer reward type</span>
          <select
            className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
            value={referral.referralReferredRewardType}
            onChange={(e) =>
              setReferral((c) => ({
                ...c,
                referralReferredRewardType: e.target.value as 'points' | 'store_credit',
              }))
            }
          >
            <option value="points">Loyalty points</option>
            <option value="store_credit">Store credit</option>
          </select>
        </label>
        {referral.referralReferrerRewardType === 'points' ? (
          <label className="block">
            <span className="text-sm font-medium text-slate-700">Points for referrer</span>
            <input
              type="number"
              min={0}
              step="1"
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
              value={referral.referralReferrerPoints}
              onChange={(e) => setReferral((c) => ({ ...c, referralReferrerPoints: e.target.value }))}
            />
          </label>
        ) : (
          <label className="block">
            <span className="text-sm font-medium text-slate-700">Store credit for referrer ($)</span>
            <input
              type="number"
              min={0}
              step="0.01"
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
              value={referral.referralReferrerCredit}
              onChange={(e) => setReferral((c) => ({ ...c, referralReferrerCredit: e.target.value }))}
            />
          </label>
        )}
        {referral.referralReferredRewardType === 'points' ? (
          <label className="block">
            <span className="text-sm font-medium text-slate-700">Points for referred customer</span>
            <input
              type="number"
              min={0}
              step="1"
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
              value={referral.referralReferredPoints}
              onChange={(e) => setReferral((c) => ({ ...c, referralReferredPoints: e.target.value }))}
            />
          </label>
        ) : (
          <label className="block">
            <span className="text-sm font-medium text-slate-700">
              Store credit for referred customer ($)
            </span>
            <input
              type="number"
              min={0}
              step="0.01"
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
              value={referral.referralReferredCredit}
              onChange={(e) => setReferral((c) => ({ ...c, referralReferredCredit: e.target.value }))}
            />
          </label>
        )}
        <label className="block">
          <span className="text-sm font-medium text-slate-700">Award trigger</span>
          <select
            className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
            value={referral.referralTrigger}
            onChange={(e) =>
              setReferral((c) => ({
                ...c,
                referralTrigger: e.target.value as 'first_visit' | 'first_purchase' | 'manual',
              }))
            }
          >
            <option value="first_visit">On customer signup / first visit</option>
            <option value="first_purchase">On first purchase / payment</option>
            <option value="manual">Manual only (no auto award)</option>
          </select>
          <p className="mt-1 text-xs text-slate-500">
            Signup awards when a new customer is saved with a referring customer linked. First purchase
            waits until checkout.
          </p>
        </label>
        <label className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3 text-sm font-medium md:col-span-2">
          <span>Notify referrer by SMS when a reward is awarded</span>
          <input
            type="checkbox"
            checked={referral.referralNotifyReferrer}
            onChange={(e) =>
              setReferral((c) => ({ ...c, referralNotifyReferrer: e.target.checked }))
            }
          />
        </label>
      </fieldset>
      <button
        type="button"
        className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
        disabled={referralMutation.isPending}
        onClick={() => referralMutation.mutate()}
      >
        {referralMutation.isPending ? 'Saving…' : 'Save Referral Settings'}
      </button>
    </section>
    </div>
  );
}

function StaffSettings() {
  const queryClient = useQueryClient();
  const usersQuery = useQuery({ queryKey: ['users'], queryFn: listUsers });
  const storesQuery = useQuery({ queryKey: ['stores'], queryFn: listStores });
  const [modal, setModal] = useState<'invite' | 'edit' | null>(null);
  const [editing, setEditing] = useState<UserListItem | null>(null);
  const [tempPasswordValue, setTempPasswordValue] = useState('');
  const [form, setForm] = useState<Record<string, string>>({});
  const selectedStores = (form.storeIds ?? '').split(',').filter(Boolean);

  const createMutation = useMutation({
    mutationFn: () => {
      const password = tempPassword();
      setTempPasswordValue(password);
      return createUser({
        email: form.email,
        firstName: form.firstName,
        lastName: form.lastName,
        role: form.role || 'front_desk',
        storeIds: selectedStores,
        password,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toastSuccess('Staff member created');
    },
    onError: (error) => {
      const parsed = parseApiError(error);
      toastError(parsed.message, parsed.code);
    },
  });

  const updateMutation = useMutation({
    mutationFn: () =>
      updateUser(editing!.id, {
        firstName: form.firstName || editing?.firstName,
        lastName: form.lastName || editing?.lastName,
        role: form.role || editing?.role,
        storeIds: selectedStores.length ? selectedStores : (editing?.storeIds ?? null),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setModal(null);
      setEditing(null);
      toastSuccess('Staff member updated');
    },
    onError: (error) => {
      const parsed = parseApiError(error);
      toastError(parsed.message, parsed.code);
    },
  });

  function openEdit(user: UserListItem) {
    setEditing(user);
    setForm({
      firstName: user.firstName,
      lastName: user.lastName,
      role: user.role,
      storeIds: (user.storeIds ?? []).join(','),
    });
    setModal('edit');
  }

  function deactivate(user: UserListItem) {
    if (
      !window.confirm(
        `Deactivate ${user.firstName} ${user.lastName}? They will no longer be able to log in.`,
      )
    )
      return;
    updateUser(user.id, { active: false })
      .then(() => {
        queryClient.invalidateQueries({ queryKey: ['users'] });
        toastSuccess('Staff member deactivated');
      })
      .catch((error) => {
        const parsed = parseApiError(error);
        toastError(parsed.message, parsed.code);
      });
  }

  function toggleStore(id: string) {
    const next = selectedStores.includes(id)
      ? selectedStores.filter((storeId) => storeId !== id)
      : [...selectedStores, id];
    setForm((current) => ({ ...current, storeIds: next.join(',') }));
  }

  return (
    <div className="space-y-6">
      <EstimatedProfitVisibilitySettings />
      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-slate-950">Staff</h2>
        <button
          className="rounded-xl bg-primary px-4 py-2 font-semibold text-white"
          onClick={() => {
            setForm({ role: 'front_desk' });
            setTempPasswordValue('');
            setModal('invite');
          }}
        >
          + Invite Staff
        </button>
      </div>
      <table className="mt-4 min-w-full divide-y divide-slate-200 text-sm">
        <thead className="text-left text-xs font-semibold uppercase text-slate-500">
          <tr>
            <th className="py-2">Name</th>
            <th>Email</th>
            <th>Role</th>
            <th>Active</th>
            <th className="text-right">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {usersQuery.data?.map((user) => (
            <tr key={user.id}>
              <td className="py-3 font-semibold">
                {user.firstName} {user.lastName}
              </td>
              <td>{user.email}</td>
              <td>{prettyLabel(user.role)}</td>
              <td>{user.active === false ? 'No' : 'Yes'}</td>
              <td className="text-right">
                <button className="mr-3 font-semibold text-primary" onClick={() => openEdit(user)}>
                  Edit
                </button>
                <button className="font-semibold text-red-600" onClick={() => deactivate(user)}>
                  Deactivate
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {modal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4">
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
            <h3 className="text-lg font-bold text-slate-950">
              {modal === 'invite' ? 'Invite Staff' : 'Edit Staff'}
            </h3>
            <div className="mt-4 grid gap-3 md:grid-cols-2">
              {modal === 'invite' ? (
                <label className="block md:col-span-2">
                  <span className="text-sm font-medium">Email</span>
                  <input
                    className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
                    value={form.email ?? ''}
                    onChange={(e) => setForm((c) => ({ ...c, email: e.target.value }))}
                  />
                </label>
              ) : null}
              <label className="block">
                <span className="text-sm font-medium">First name</span>
                <input
                  className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
                  value={form.firstName ?? ''}
                  onChange={(e) => setForm((c) => ({ ...c, firstName: e.target.value }))}
                />
              </label>
              <label className="block">
                <span className="text-sm font-medium">Last name</span>
                <input
                  className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
                  value={form.lastName ?? ''}
                  onChange={(e) => setForm((c) => ({ ...c, lastName: e.target.value }))}
                />
              </label>
              <label className="block md:col-span-2">
                <span className="text-sm font-medium">Role</span>
                <select
                  className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
                  value={form.role ?? 'front_desk'}
                  onChange={(e) => setForm((c) => ({ ...c, role: e.target.value }))}
                >
                  <option value="front_desk">Front Desk</option>
                  <option value="technician">Technician</option>
                  <option value="store_manager">Store Manager</option>
                  <option value="tenant_admin">Tenant Admin</option>
                </select>
              </label>
              <div className="md:col-span-2">
                <span className="text-sm font-medium">Store access</span>
                <div className="mt-2 space-y-2 rounded-xl border border-slate-200 p-3">
                  {storesQuery.data?.map((store) => (
                    <label key={store.id} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={selectedStores.includes(store.id)}
                        onChange={() => toggleStore(store.id)}
                      />
                      {store.name}
                    </label>
                  ))}
                </div>
              </div>
            </div>
            {tempPasswordValue ? (
              <div className="mt-4 rounded-xl bg-green-50 p-3 text-sm font-semibold text-green-800">
                User created. Temporary password: {tempPasswordValue}
              </div>
            ) : null}
            <div className="mt-5 flex justify-end gap-2">
              <button
                className="rounded-xl border border-slate-300 px-4 py-2 font-semibold"
                onClick={() => {
                  setModal(null);
                  setEditing(null);
                }}
              >
                Close
              </button>
              <button
                className="rounded-xl bg-primary px-4 py-2 font-semibold text-white disabled:opacity-50"
                disabled={createMutation.isPending || updateMutation.isPending}
                onClick={() =>
                  modal === 'invite' ? createMutation.mutate() : updateMutation.mutate()
                }
              >
                {modal === 'invite' ? 'Send Invite' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      </section>
    </div>
  );
}

function CashSessionSettings() {
  const queryClient = useQueryClient();
  const [reportId, setReportId] = useState<string | null>(null);
  const storeQuery = useQuery({ queryKey: ['store-current'], queryFn: getCurrentStore });
  const store = storeQuery.data;
  const sessionsQuery = useQuery({
    queryKey: ['cash-sessions'],
    queryFn: () => listCashSessions({ page: 1, pageSize: 50, status: 'closed' }),
  });
  const sessions = sessionsQuery.data?.data ?? [];
  const tolerance = Number(store?.cashVarianceTolerance ?? 5);

  const denomConfig = useMemo(
    () => resolveCashDenominationConfig(store?.currency, store?.cashDenominations ?? null),
    [store?.currency, store?.cashDenominations],
  );

  const [draft, setDraft] = useState(denomConfig);
  const [trackRolls, setTrackRolls] = useState(false);
  const [varTol, setVarTol] = useState('5.00');
  const [requireOverride, setRequireOverride] = useState(true);
  const [changeAlerts, setChangeAlerts] = useState(false);
  const [minimums, setMinimums] = useState<Record<string, string>>({});
  const [largeBillThr, setLargeBillThr] = useState('400.00');
  const [dropThr, setDropThr] = useState('1000.00');
  const [smsCritical, setSmsCritical] = useState(false);
  const [dedupeHours, setDedupeHours] = useState('4');
  const [dropAlertMode, setDropAlertMode] = useState<'never' | 'on_close' | 'realtime'>('on_close');

  useEffect(() => {
    if (!store) return;
    setDraft(resolveCashDenominationConfig(store.currency, store.cashDenominations ?? null));
    setTrackRolls(store.trackCoinRolls ?? false);
    setVarTol(String(store.cashVarianceTolerance ?? '5.00'));
    setRequireOverride(store.cashVarianceRequiresOverride ?? true);
    setChangeAlerts(store.changeAlertsEnabled ?? false);
    const mins = store.changeMinimums ?? {};
    const asStr: Record<string, string> = {};
    for (const [k, v] of Object.entries(mins)) asStr[k] = String(v);
    setMinimums(asStr);
    setLargeBillThr(String(store.largeBillThreshold ?? '400.00'));
    setDropThr(String(store.dropThreshold ?? '1000.00'));
    setSmsCritical(store.changeAlertsSmsOnCritical ?? false);
    setDedupeHours(String(store.changeAlertDedupeHours ?? 4));
    setDropAlertMode(
      (store.dropAlertMode as 'never' | 'on_close' | 'realtime' | null) ?? 'on_close',
    );
  }, [store]);

  const usageQuery = useQuery({
    queryKey: ['cash-change-usage'],
    queryFn: () => cashSessionsApi.getChangeUsage(30),
    enabled: Boolean(store),
  });

  const saveMutation = useMutation({
    mutationFn: (body: StoreSettingsUpdate) => updateCurrentStore(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['store-current'] });
      queryClient.invalidateQueries({ queryKey: ['cash-denomination-settings'] });
      toastSuccess('Cash drawer settings saved');
    },
    onError: (error) => {
      const parsed = parseApiError(error);
      toastError(parsed.message, parsed.code);
    },
  });

  function varianceTextClass(value: string | null | undefined) {
    const amount = Math.abs(Number(value ?? 0));
    if (amount === 0) return 'font-semibold text-green-700';
    if (amount <= tolerance) return 'font-semibold text-amber-700';
    return 'font-semibold text-red-700';
  }

  function toggleUnit(kind: 'bills' | 'coins', key: string) {
    setDraft((prev) => ({
      ...prev,
      [kind]: prev[kind].map((u) => (u.key === key ? { ...u, enabled: !u.enabled } : u)),
    }));
  }

  function buildChangeMinimums(): Record<string, number> {
    const changeMinimums: Record<string, number> = {};
    for (const [k, raw] of Object.entries(minimums)) {
      const n = Math.trunc(Number(raw));
      if (Number.isFinite(n) && n > 0) changeMinimums[k] = n;
    }
    return changeMinimums;
  }

  function saveDrawerCountSettings() {
    saveMutation.mutate({
      cashDenominations: {
        bills: draft.bills,
        coins: draft.coins,
        rollValues: draft.rollValues,
      },
      trackCoinRolls: trackRolls,
      cashVarianceTolerance: Number(varTol),
      cashVarianceRequiresOverride: requireOverride,
    });
  }

  function saveChangeDropAlertSettings() {
    saveMutation.mutate({
      changeAlertsEnabled: changeAlerts,
      changeMinimums: buildChangeMinimums(),
      largeBillThreshold: Number(largeBillThr),
      dropThreshold: Number(dropThr),
      changeAlertsSmsOnCritical: smsCritical,
      changeAlertDedupeHours: Math.trunc(Number(dedupeHours) || 4),
      dropAlertMode,
    });
  }

  return (
    <div className="space-y-6">
      <ChecklistItemsSettings
        openingItems={store?.openingChecklistItems ?? null}
        closingItems={store?.closingChecklistItems ?? null}
        openingEnabled={Boolean(store?.openingChecklistEnabled)}
        closingEnabled={Boolean(store?.closingChecklistEnabled)}
        onToggleEnabled={(patch) => {
          void updateCurrentStore(patch)
            .then(() => {
              queryClient.invalidateQueries({ queryKey: ['store-current'] });
              toastSuccess('Checklist setting updated');
            })
            .catch((e) => toastError(parseApiError(e).message));
        }}
      />

      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-xl font-bold text-slate-950">Drawer count settings</h2>
        <p className="mt-1 text-sm text-slate-500">
          Denominations follow store currency ({store?.currency ?? 'USD'}). Hide units this store never
          counts. Variance over tolerance requires a closing note
          {requireOverride ? ' and manager override' : ''}.
        </p>

        <div className="mt-5 grid gap-6 lg:grid-cols-2">
          <div>
            <h3 className="text-sm font-bold text-slate-800">Bills shown on count</h3>
            <ul className="mt-2 space-y-1">
              {draft.bills.map((b) => (
                <li key={b.key}>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={b.enabled}
                      onChange={() => toggleUnit('bills', b.key)}
                    />
                    {b.label}
                  </label>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="text-sm font-bold text-slate-800">Coins shown on count</h3>
            <ul className="mt-2 space-y-1">
              {draft.coins.map((c) => (
                <li key={c.key}>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={c.enabled}
                      onChange={() => toggleUnit('coins', c.key)}
                    />
                    {c.label}
                  </label>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-5 grid gap-4 sm:grid-cols-3">
          <label className="flex items-center gap-2 text-sm font-medium">
            <input
              type="checkbox"
              checked={trackRolls}
              onChange={(e) => setTrackRolls(e.target.checked)}
            />
            Track coin rolls
          </label>
          <label className="block text-sm">
            <span className="font-medium text-slate-700">Variance tolerance ($)</span>
            <input
              type="number"
              min="0"
              step="0.01"
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
              value={varTol}
              onChange={(e) => setVarTol(e.target.value)}
            />
          </label>
          <label className="flex items-center gap-2 text-sm font-medium">
            <input
              type="checkbox"
              checked={requireOverride}
              onChange={(e) => setRequireOverride(e.target.checked)}
            />
            Require manager override over tolerance
          </label>
        </div>

        <div className="mt-4 flex justify-end">
          <button
            type="button"
            className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            disabled={saveMutation.isPending}
            onClick={saveDrawerCountSettings}
          >
            {saveMutation.isPending ? 'Saving…' : 'Save drawer settings'}
          </button>
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-xl font-bold text-slate-950">Change & drop alerts</h2>
        <p className="mt-1 text-sm text-slate-500">
          Low-change alerts use per-denomination <em>counts</em>. Drop alerts use dollar thresholds.
          Alerts are advisory and never block open/close.
        </p>

        <div className="mt-4 flex flex-wrap gap-4">
          <label className="flex items-center gap-2 text-sm font-medium">
            <input
              type="checkbox"
              checked={changeAlerts}
              onChange={(e) => {
                setChangeAlerts(e.target.checked);
                if (e.target.checked && Object.keys(minimums).length === 0) {
                  const seeded: Record<string, string> = {};
                  for (const [k, v] of Object.entries(DEFAULT_CHANGE_MINIMUMS)) {
                    seeded[k] = String(v);
                  }
                  setMinimums(seeded);
                }
              }}
            />
            Enable change / drop alerts
          </label>
          <label className="flex items-center gap-2 text-sm font-medium">
            <input
              type="checkbox"
              checked={smsCritical}
              onChange={(e) => setSmsCritical(e.target.checked)}
              disabled={!changeAlerts}
            />
            SMS managers on critical only
          </label>
          <label className="block text-sm">
            <span className="font-medium text-slate-700">Dedupe window (hours)</span>
            <input
              type="number"
              min="0"
              max="168"
              className="mt-1 w-24 rounded-lg border border-slate-300 px-2 py-1.5"
              value={dedupeHours}
              disabled={!changeAlerts}
              onChange={(e) => setDedupeHours(e.target.value)}
            />
          </label>
        </div>

        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <label className="block text-sm">
            <span className="font-medium text-slate-700">Large bill ($50+) threshold ($)</span>
            <input
              type="number"
              min="0"
              step="0.01"
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
              value={largeBillThr}
              disabled={!changeAlerts}
              onChange={(e) => setLargeBillThr(e.target.value)}
            />
          </label>
          <label className="block text-sm">
            <span className="font-medium text-slate-700">Drawer total drop threshold ($)</span>
            <input
              type="number"
              min="0"
              step="0.01"
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
              value={dropThr}
              onChange={(e) => setDropThr(e.target.value)}
            />
            <span className="mt-1 block text-xs text-slate-500">
              Used for close-screen and mid-day drop alerts.
            </span>
          </label>
          <label className="block text-sm sm:col-span-2">
            <span className="font-medium text-slate-700">Drop alert timing</span>
            <select
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
              value={dropAlertMode}
              onChange={(e) =>
                setDropAlertMode(e.target.value as 'never' | 'on_close' | 'realtime')
              }
            >
              <option value="never">Never — no drawer-total drop notifications</option>
              <option value="on_close">On open/close only (default)</option>
              <option value="realtime">
                Realtime — after each cash sale, once per accumulation (re-arms after cash out)
              </option>
            </select>
            <span className="mt-1 block text-xs text-slate-500">
              Realtime notifies managers when expected drawer crosses the drop threshold mid-day —
              not after every payment. Leave on close-only for quieter stores.
            </span>
          </label>
        </div>

        <h3 className="mt-5 text-sm font-bold text-slate-800">Minimum counts (blank = no alert)</h3>
        <div className="mt-2 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {[...draft.bills, ...draft.coins]
            .filter((u) => u.enabled)
            .map((u) => (
              <label key={u.key} className="flex items-center justify-between gap-2 text-sm">
                <span className="font-medium text-slate-700">{u.label}</span>
                <input
                  type="number"
                  min="0"
                  step="1"
                  inputMode="numeric"
                  className="w-20 rounded-lg border border-slate-300 px-2 py-1 text-right"
                  placeholder="—"
                  disabled={!changeAlerts}
                  value={minimums[u.key] ?? ''}
                  onChange={(e) =>
                    setMinimums((prev) => ({
                      ...prev,
                      [u.key]: e.target.value,
                    }))
                  }
                />
              </label>
            ))}
        </div>

        {(usageQuery.data?.suggestions?.length ?? 0) > 0 ? (
          <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-4">
            <h3 className="text-sm font-bold text-slate-900">Suggested minimums from history</h3>
            <p className="mt-1 text-xs text-slate-500">{usageQuery.data?.note}</p>
            <ul className="mt-3 space-y-2 text-sm">
              {usageQuery.data!.suggestions.map((s) => (
                <li
                  key={s.denomination}
                  className="flex flex-wrap items-center justify-between gap-2"
                >
                  <span>
                    {s.label}: avg {s.averageConsumedPerSession}/session → suggested{' '}
                    <strong>{s.suggestedMinimum}</strong>
                  </span>
                  <button
                    type="button"
                    className="text-sm font-semibold text-primary"
                    disabled={!changeAlerts}
                    onClick={() =>
                      setMinimums((prev) => ({
                        ...prev,
                        [s.denomination]: String(s.suggestedMinimum),
                      }))
                    }
                  >
                    Apply
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <div className="mt-6 flex justify-end border-t border-slate-200 pt-4">
          <button
            type="button"
            className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            disabled={saveMutation.isPending}
            onClick={saveChangeDropAlertSettings}
          >
            {saveMutation.isPending ? 'Saving…' : 'Save alert settings'}
          </button>
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-xl font-bold text-slate-950">Cash Sessions</h2>
        <p className="mt-1 text-sm text-slate-500">
          Review closed drawer sessions and Z-report data. Red variance exceeds tolerance of{' '}
          {formatCurrency(tolerance)}.
        </p>
        <div className="mt-4 overflow-x-auto rounded-xl border border-slate-200">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50 text-left text-xs font-semibold uppercase text-slate-500">
              <tr>
                <th className="px-4 py-3">Date</th>
                <th>Opened By</th>
                <th>Opened At</th>
                <th>Closed At</th>
                <th>Float</th>
                <th>Expected</th>
                <th>Counted</th>
                <th>Variance</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {sessions.map((session) => (
                <tr
                  key={session.id}
                  className={session.overTolerance ? 'bg-red-50/60' : undefined}
                >
                  <td className="px-4 py-3">{formatDate(session.openedAt)}</td>
                  <td>{session.openedByName ?? '-'}</td>
                  <td>{formatDateTime(session.openedAt)}</td>
                  <td>{session.closedAt ? formatDateTime(session.closedAt) : '-'}</td>
                  <td>{formatCurrency(session.openingFloat)}</td>
                  <td>{formatCurrency(session.expectedCash ?? session.summary.expectedCash)}</td>
                  <td>{session.closingCount ? formatCurrency(session.closingCount) : '-'}</td>
                  <td className={varianceTextClass(session.variance)}>
                    {session.variance != null ? formatCurrency(session.variance) : '-'}
                    {session.overTolerance ? ' ⚠' : ''}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      className="font-semibold text-primary"
                      onClick={() => setReportId(session.id)}
                    >
                      View Z-Report
                    </button>
                  </td>
                </tr>
              ))}
              {!sessions.length && !sessionsQuery.isLoading ? (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center text-slate-500">
                    No cash sessions found.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
        <CashSessionReportModal sessionId={reportId} onClose={() => setReportId(null)} />
      </section>
    </div>
  );
}

function Placeholder({ title }: { title: string }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 text-slate-500 shadow-sm">
      {title} coming soon.
    </div>
  );
}

export function SettingsPage() {
  return (
    <SettingsLayout>
      <Routes>
        <Route index element={<Navigate to="store" replace />} />
        <Route path="store" element={<StoreInfoSettingsPage />} />
        <Route path="printing" element={<PrintingSettingsPage />} />
        <Route path="payments" element={<PaymentsSettingsPage />} />
        <Route path="integrations" element={<IntegrationsSettingsPage />} />
        <Route path="pins" element={<PinSettingsPage />} />
        <Route path="locations" element={<LocationsSettingsPage />} />
        <Route path="conditions" element={<ConditionsSettingsPage />} />
        <Route path="devices" element={<DeviceCatalogSettingsPage />} />
        <Route path="inventory-categories" element={<InventoryCategoriesSettingsPage />} />
        <Route path="suppliers" element={<SuppliersSettingsPage />} />
        <Route path="fee-presets" element={<FeePresetsSettingsPage />} />
        <Route path="discounts" element={<DiscountPresetsSettingsPage />} />
        <Route path="approvals" element={<ApprovalsSettingsPage />} />
        <Route path="pricing-matrix" element={<PricingMatrixPage />} />
        <Route path="warranty-rules" element={<WarrantyRulesPage />} />
        <Route path="upsell" element={<UpsellSettingsPage />} />
        <Route path="tickets" element={<TicketSettings />} />
        <Route path="ticket-statuses" element={<TicketStatusesSettingsPage />} />
        <Route path="repair-services" element={<RepairServicesListPage />} />
        <Route path="repair-services/:id" element={<RepairServiceEditorPage />} />
        <Route path="templates" element={<TicketTemplatesSettingsPage />} />
        <Route path="cash-sessions" element={<CashSessionSettings />} />
        <Route path="staff" element={<StaffSettings />} />
        <Route path="notifications" element={<NotificationSettings />} />
        <Route path="appointments" element={<AppointmentsSettingsPage />} />
        <Route path="care-guides" element={<CareGuidesSettingsPage />} />
        <Route path="duplicate-customers" element={<DuplicateCustomersSettingsPage />} />
        <Route path="audit-log" element={<AuditLogSettingsPage />} />
        <Route path="waivers" element={<WaiverSettings />} />
        <Route path="loyalty" element={<LoyaltySettings />} />
        <Route path="pos" element={<PosSettingsPage />} />
        <Route path="snippets" element={<SnippetsSettingsPage />} />
        <Route path="forms" element={<FormsSettingsPage />} />
        <Route path="forms/new" element={<FormBuilderPage />} />
        <Route path="forms/:id/edit" element={<FormBuilderPage />} />
        <Route path="profile" element={<ProfileSettingsPage />} />
        <Route path="permissions" element={<PermissionsSettingsPage />} />
        <Route path="migration" element={<MigrationSettingsPage />} />
        <Route path="migration/:jobId" element={<MigrationSettingsPage />} />
        <Route path="data-cleanup/balances" element={<DataCleanupBalancesPage />} />
        <Route path="data-cleanup/parts-costs" element={<DataCleanupPartsCostsPage />} />
        <Route path="data-cleanup/inventory-duplicates" element={<InventoryDuplicatesPage />} />
        <Route path="data-cleanup/device-compatibility" element={<DeviceCompatibilityCleanupPage />} />
        <Route path="billing" element={<Placeholder title="Billing" />} />
      </Routes>
    </SettingsLayout>
  );
}
